import { db, dbLogs } from '../firebase'
import {
  buildInventoryArrivalConfirmationNoteText,
  buildInitialInventoryNoteText,
  buildInventoryBillingNoteText,
  buildInventoryDepartureNoteText,
  buildInventoryOrderFromSource,
  buildManualInventoryOrder,
  createInventoryNote
} from './inventoryOrderFactory'
import {
  INVENTORY_LOT_NAME_MAX_LENGTH,
  INVENTORY_TERM,
  canBillInventory,
  canConfirmInventoryArrival,
  canLeaveInventory,
  canSendToInventory,
  getLatestHistoryEntryWithKey,
  getValidLatestDropPhotos,
  isActiveInventoryOrder,
  isInventoryLot,
  isInventoryOrder,
  isInventoryTerm,
  isValidInventoryLotName,
  normalizeInventoryLotName,
  resolveInventoryLots
} from './inventorySelectors'

const INVALID_FIREBASE_KEY_CHARACTERS = ['.', '#', '$', '[', ']', '/']

export class InventoryServiceError extends Error {
  constructor (code, message, details) {
    super(message)
    this.name = 'InventoryServiceError'
    this.code = code
    this.details = details || {}
  }
}

const valueFromSnapshot = snapshot => snapshot && typeof snapshot.val === 'function' ? snapshot.val() : null

const snapshotExists = snapshot => {
  if (!snapshot) return false
  if (typeof snapshot.exists === 'function') return snapshot.exists()
  return valueFromSnapshot(snapshot) !== null
}

const asText = value => {
  if (value === null || typeof value === 'undefined') return ''
  return String(value)
}

const asNumber = (value, fallback = 0) => {
  const parsed = Number(value)
  return isFinite(parsed) ? parsed : fallback
}

const validTimestamp = (value, fieldName) => {
  const parsed = Number(value)
  if (!isFinite(parsed) || parsed <= 0) {
    throw new InventoryServiceError(
      'INVALID_TIMESTAMP',
      `${fieldName} must be a positive Unix timestamp in milliseconds`,
      {fieldName, value}
    )
  }
  return parsed
}

const timestampOrNow = value => {
  return value === null || typeof value === 'undefined' || value === '' ? Date.now() : value
}

const assertFirebaseKey = (value, fieldName) => {
  const key = asText(value).trim()
  const hasInvalidCharacter = INVALID_FIREBASE_KEY_CHARACTERS.some(character => key.indexOf(character) !== -1)
  if (!key || hasInvalidCharacter) {
    throw new InventoryServiceError(
      'INVALID_FIREBASE_KEY',
      `${fieldName} is not a valid Firebase key`,
      {fieldName, value}
    )
  }
  return key
}

const uniqueIds = (value, singularValue, fieldName) => {
  const values = Array.isArray(value) ? value : (singularValue ? [singularValue] : [])
  const ids = values.map(item => assertFirebaseKey(item, fieldName))
  if (!ids.length) {
    throw new InventoryServiceError('EMPTY_SELECTION', `At least one ${fieldName} is required`)
  }
  const duplicates = ids.filter((id, index) => ids.indexOf(id) !== index)
  if (duplicates.length) {
    throw new InventoryServiceError('DUPLICATE_ORDER_IDS', 'Duplicate order IDs are not allowed', {
      orderIds: Array.from(new Set(duplicates))
    })
  }
  return ids
}

const normalizePerson = person => {
  const value = person || {}
  return {
    uid: asText(value.uid || value.key || value['.key'] || value.chauffeurUid || value.chauffeur).trim(),
    name: asText(value.name || value.value || value.chauffeurName).trim()
  }
}

const requireDriver = driver => {
  const normalized = normalizePerson(driver)
  if (!normalized.uid || !normalized.name) {
    throw new InventoryServiceError(
      'DRIVER_REQUIRED',
      'A dispatch driver with uid and name is required',
      {driver}
    )
  }
  return normalized
}

const normalizeOperator = operator => normalizePerson(operator)

const operatorNameFromRecord = record => {
  const value = record || {}
  const fullName = [value.firstName, value.lastName]
    .map(asText)
    .map(part => part.trim())
    .filter(Boolean)
    .join(' ')
  return fullName || asText(value.name || value.displayName || value.email).trim()
}

const resolveOperator = async (database, companyUID, operator) => {
  const fallback = normalizeOperator(operator)
  if (!fallback.uid) return fallback

  try {
    const snapshot = await database.ref(`userInfos/${companyUID}/${fallback.uid}`).once('value')
    if (!snapshotExists(snapshot)) return fallback
    return {
      uid: fallback.uid,
      name: operatorNameFromRecord(valueFromSnapshot(snapshot)) || fallback.name
    }
  } catch (error) {
    return fallback
  }
}

const cleanFirebaseValue = value => {
  if (value === null) return null
  if (Array.isArray(value)) {
    return value
      .filter(item => typeof item !== 'undefined' && typeof item !== 'function')
      .map(cleanFirebaseValue)
  }
  if (value && typeof value === 'object') {
    return Object.keys(value).reduce((result, key) => {
      if (key.charAt(0) === '.' || typeof value[key] === 'undefined' || typeof value[key] === 'function') return result
      result[key] = cleanFirebaseValue(value[key])
      return result
    }, {})
  }
  return value
}

const errorMessage = error => {
  if (!error) return 'Unknown dispatch queue error'
  return error.message || String(error)
}

const getPushKey = ref => {
  const pushedRef = ref.push()
  if (!pushedRef || !pushedRef.key) {
    throw new InventoryServiceError('PUSH_KEY_FAILED', 'Firebase did not generate a push key')
  }
  return {key: pushedRef.key, ref: pushedRef}
}

const formatLegacyDispatchDate = value => {
  const date = new Date(value)
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
}

const joinComments = (...comments) => {
  return comments
    .map(comment => asText(comment).trim())
    .filter(Boolean)
    .join(' | ')
}

const retryDispatchComment = order => {
  if (order && order.dateLeft && order.inventoryExitRequesterName && order.inventoryExitRequesterOrganization) {
    return joinComments(order.comment, buildInventoryDepartureNoteText({
      requesterName: order.inventoryExitRequesterName,
      requesterOrganization: order.inventoryExitRequesterOrganization,
      comment: order.inventoryExitComment
    }))
  }
  return asText(order && order.comment)
}

const readDelivery = async (database, companyUID, orderId) => {
  const snapshot = await database
    .ref(`database/transporter/${companyUID}/deliveries/${orderId}`)
    .once('value')
  return snapshotExists(snapshot) ? valueFromSnapshot(snapshot) : null
}

const readDeliveries = async (database, companyUID, orderIds) => {
  const deliveries = await Promise.all(orderIds.map(orderId => readDelivery(database, companyUID, orderId)))
  return orderIds.map((orderId, index) => ({orderId, order: deliveries[index]}))
}

const normalizeVin = vin => asText(vin).replace(/\s/g, '').toUpperCase()

const snapshotChildren = snapshot => {
  const children = []
  if (snapshot && typeof snapshot.forEach === 'function') {
    snapshot.forEach(child => {
      children.push({orderId: child.key, order: valueFromSnapshot(child)})
    })
    return children
  }

  const value = valueFromSnapshot(snapshot) || {}
  return Object.keys(value).map(orderId => ({orderId, order: value[orderId]}))
}

export const findActiveInventoryVinDuplicates = async options => {
  const config = options || {}
  const database = config.database || db
  const companyUID = assertFirebaseKey(config.companyUID, 'companyUID')
  const requestedVins = (Array.isArray(config.vins) ? config.vins : [config.vin])
    .map(normalizeVin)
    .filter(Boolean)
  if (!requestedVins.length) return []

  const requested = Array.from(new Set(requestedVins))
  const inventoryRef = database
    .ref(`database/transporter/${companyUID}/deliveries`)
    .orderByChild('isInventory')
    .equalTo(true)
  const snapshot = await inventoryRef.once('value')
  const activeByVin = snapshotChildren(snapshot).reduce((result, entry) => {
    if (!entry.order || !isActiveInventoryOrder(entry.order)) return result
    const vin = normalizeVin(entry.order.vin)
    if (!vin || requested.indexOf(vin) === -1) return result
    if (!result[vin]) result[vin] = []
    result[vin].push(entry.orderId)
    return result
  }, {})

  return requested
    .filter(vin => activeByVin[vin] && activeByVin[vin].length)
    .map(vin => ({vin, orderIds: activeByVin[vin]}))
}

const validateInventoryVins = async ({database, companyUID, entries}) => {
  const vins = entries.map(entry => normalizeVin(entry.order.vin)).filter(Boolean)
  const duplicateBatchVins = Array.from(new Set(vins.filter((vin, index) => vins.indexOf(vin) !== index)))
  if (duplicateBatchVins.length) {
    throw new InventoryServiceError(
      'DUPLICATE_VIN_IN_BATCH',
      `The same VIN appears more than once in this batch: ${duplicateBatchVins.join(', ')}`,
      {vins: duplicateBatchVins}
    )
  }

  const activeDuplicates = await findActiveInventoryVinDuplicates({database, companyUID, vins})
  if (activeDuplicates.length) {
    throw new InventoryServiceError(
      'ACTIVE_INVENTORY_VIN_DUPLICATE',
      'One or more VINs already have an active unbilled inventory order',
      {duplicates: activeDuplicates}
    )
  }
}

const writeAuditLogs = async ({logsDatabase, companyUID, operator, entries}) => {
  if (!logsDatabase || !entries.length) return []
  const person = normalizeOperator(operator)
  return Promise.all(entries.map(async entry => {
    try {
      const logRef = logsDatabase.ref(`logs/transporter/${companyUID}`).push()
      await logRef.set({
        UID: person.uid,
        action: entry.action,
        orderID: entry.orderId,
        sourceOrderID: entry.sourceOrderId || '',
        date: entry.date,
        ...(entry.inventoryLot ? {inventoryLot: entry.inventoryLot} : {})
      })
      return {orderId: entry.orderId, logged: true}
    } catch (error) {
      return {orderId: entry.orderId, logged: false, error, errorMessage: errorMessage(error)}
    }
  }))
}

const loadInventoryLots = async (database, companyUID) => {
  const snapshot = await database.ref(`database/transporter/${companyUID}/inventoryLots`).once('value')
  return resolveInventoryLots(snapshotExists(snapshot) ? valueFromSnapshot(snapshot) : null)
}

export const readInventoryLots = async options => {
  const config = options || {}
  const database = config.database || db
  const companyUID = assertFirebaseKey(config.companyUID, 'companyUID')
  return loadInventoryLots(database, companyUID)
}

const validateInventoryLots = async ({database, companyUID, entries}) => {
  const lots = await loadInventoryLots(database, companyUID)
  const invalidLots = entries
    .map(entry => normalizeInventoryLotName(entry.order.inventoryLot))
    .filter(lot => !isInventoryLot(lot, lots))
  if (invalidLots.length) {
    throw new InventoryServiceError(
      'INVALID_INVENTORY_LOT',
      `Unknown inventory lot: ${Array.from(new Set(invalidLots)).join(', ')}`,
      {inventoryLots: Array.from(new Set(invalidLots))}
    )
  }
}

const requireInventoryLotName = (value, fieldName) => {
  const name = normalizeInventoryLotName(value)
  if (!isValidInventoryLotName(name)) {
    throw new InventoryServiceError(
      'INVALID_INVENTORY_LOT_NAME',
      `${fieldName} is required and must be at most ${INVENTORY_LOT_NAME_MAX_LENGTH} characters`,
      {fieldName, value}
    )
  }
  return name
}

const lotAuthorFields = (prefix, operator, date) => ({
  [`${prefix}At`]: date,
  [`${prefix}ByUid`]: operator.uid,
  [`${prefix}ByName`]: operator.name
})

const findInventoryLot = (lots, lotName) => {
  const name = normalizeInventoryLotName(lotName)
  const lot = lots.find(item => item.name === name)
  if (!lot) {
    throw new InventoryServiceError('INVENTORY_LOT_NOT_FOUND', `Inventory lot ${name} does not exist`, {inventoryLot: name})
  }
  return lot
}

const readInventoryOrders = async (database, companyUID) => {
  const snapshot = await database
    .ref(`database/transporter/${companyUID}/deliveries`)
    .orderByChild('isInventory')
    .equalTo(true)
    .once('value')
  return snapshotChildren(snapshot).filter(entry => entry.order && isInventoryOrder(entry.order))
}

const lotChangeUpdates = ({companyRef, orderId, previousLot, inventoryLot, operator, updatedAt, noteText}) => {
  const notePush = getPushKey(companyRef.child(`deliveries/${orderId}/notes`))
  return {
    [`deliveries/${orderId}/inventoryLot`]: inventoryLot,
    [`deliveries/${orderId}/inventoryLotUpdatedAt`]: updatedAt,
    [`deliveries/${orderId}/inventoryLotUpdatedByUid`]: operator.uid,
    [`deliveries/${orderId}/inventoryLotUpdatedByName`]: operator.name,
    [`deliveries/${orderId}/notes/${notePush.key}`]: createInventoryNote({
      date: updatedAt,
      name: operator.name,
      text: noteText || `INVENTORY LOT CHANGED - ${previousLot || 'Unassigned'} -> ${inventoryLot}`
    })
  }
}

export const allocateInventoryIds = (nextInventoryId, count) => {
  const next = Number(nextInventoryId)
  const total = Number(count)
  if (!Number.isInteger(next) || next < 1) {
    throw new InventoryServiceError('INVALID_INVENTORY_COUNTER', 'invNextID must be a positive integer', {
      value: nextInventoryId
    })
  }
  if (!Number.isInteger(total) || total < 1) {
    throw new InventoryServiceError('INVALID_BATCH_SIZE', 'Inventory batch size must be a positive integer', {
      value: count
    })
  }

  return {
    orderIds: Array.from({length: total}, (unused, index) => `INV-${next + index}`),
    nextInventoryId: next + total
  }
}

export const buildDispatchPayload = (order, driver, options = {}) => {
  if (!order || typeof order !== 'object') {
    throw new InventoryServiceError('ORDER_REQUIRED', 'An order is required to build a dispatch payload')
  }
  const selectedDriver = requireDriver(driver)
  const dispatchTimestamp = validTimestamp(timestampOrNow(options.dispatchDate), 'dispatchDate')
  const dateFormatter = typeof options.dateFormatter === 'function'
    ? options.dateFormatter
    : formatLegacyDispatchDate

  const payload = {
    chauffeur: selectedDriver.uid,
    chauffeurName: selectedDriver.name,
    nbDispatch: asNumber(order.nbDispatch, 0),
    comment: options.comment !== undefined ? asText(options.comment) : asText(order.comment),
    numRef: asText(order.numRef),
    price: options.price !== undefined ? asNumber(options.price, 0) : asNumber(order.price, 0),
    receiver: asText(order.receiverName),
    receiverAddress: asText(order.receiverAddress),
    receiverCity: asText(order.receiverCity),
    receiverContact: asText(order.receiverContact),
    receiverFax: asText(order.receiverFax),
    receiverName: asText(order.receiverName),
    receiverPhone: asText(order.receiverPhone),
    receiverEmail: asText(order.receiverEmail),
    receiverPostcode: asText(order.receiverPostcode),
    receiverProvince: asText(order.receiverProvince),
    receiverCountry: asText(order.receiverCountry),
    receiverZone: asText(order.receiverZone),
    transmitter: asText(order.transmitterName),
    transmitterAddress: asText(order.transmitterAddress),
    transmitterCity: asText(order.transmitterCity),
    transmitterContact: asText(order.transmitterContact),
    transmitterFax: asText(order.transmitterFax),
    transmitterName: asText(order.transmitterName),
    transmitterPhone: asText(order.transmitterPhone),
    transmitterEmail: asText(order.transmitterEmail),
    transmitterPostcode: asText(order.transmitterPostcode),
    transmitterProvince: asText(order.transmitterProvince),
    transmitterCountry: asText(order.transmitterCountry),
    transmitterZone: asText(order.transmitterZone),
    dateDispatch: dateFormatter(dispatchTimestamp),
    deliveryType: asText(options.deliveryType || order.deliveryType || 'delivery'),
    distance: options.distance !== undefined ? asNumber(options.distance, 0) : asNumber(order.distance, 0),
    deliveryOrder: asNumber(options.deliveryOrder, 1)
  }

  if (options.pickDate !== null && typeof options.pickDate !== 'undefined' && options.pickDate !== '') {
    payload.pickDate = validTimestamp(options.pickDate, 'pickDate')
    payload.isCancelled = false
  }

  return payload
}

const queueDispatch = async ({
  database,
  companyUID,
  orderId,
  order,
  driver,
  deliveryOrder,
  comment,
  dispatchDate,
  pickDate
}) => {
  let payload = null
  try {
    payload = buildDispatchPayload(order, driver, {
      deliveryOrder,
      comment,
      dispatchDate,
      pickDate,
      price: 0,
      distance: 0,
      deliveryType: 'delivery'
    })
    const queueRoot = database.ref(`test/transporter/${companyUID}/newDispatch/${orderId}`)
    const pushed = getPushKey(queueRoot)
    await pushed.ref.set(payload)
    return {orderId, queued: true, queueKey: pushed.key, payload}
  } catch (error) {
    return {orderId, queued: false, error, errorMessage: errorMessage(error), payload}
  }
}

const queueDispatchBatch = async ({database, companyUID, entries, driver, dispatchDate}) => {
  const dispatchResults = []
  for (let index = 0; index < entries.length; index += 1) {
    const entry = entries[index]
    dispatchResults.push(await queueDispatch({
      database,
      companyUID,
      orderId: entry.orderId,
      order: entry.order,
      driver,
      deliveryOrder: index + 1,
      comment: entry.dispatchComment,
      dispatchDate,
      pickDate: entry.pickDate
    }))
  }
  return dispatchResults
}

const normalizeCreationEntries = orders => {
  if (!Array.isArray(orders) || !orders.length) {
    throw new InventoryServiceError('EMPTY_SELECTION', 'At least one inventory order is required')
  }

  const entries = orders.map(item => {
    const wrapped = item && item.order && typeof item.order === 'object'
    const order = wrapped ? item.order : item
    if (!order || typeof order !== 'object' || !isInventoryOrder(order)) {
      throw new InventoryServiceError('INVALID_INVENTORY_ORDER', 'Every created order must have isInventory === true')
    }
    if (!normalizeVin(order.vin)) {
      throw new InventoryServiceError('INVENTORY_VIN_REQUIRED', 'Every inventory order must have a VIN')
    }
    if (!normalizeInventoryLotName(order.inventoryLot)) {
      throw new InventoryServiceError(
        'INVENTORY_LOT_REQUIRED',
        'Every inventory order must have an inventoryLot'
      )
    }
    if (!isInventoryTerm(order.inventoryTerm)) {
      throw new InventoryServiceError(
        'INVENTORY_TERM_REQUIRED',
        'Every inventory order must have an inventoryTerm of longTerm or shortTerm'
      )
    }
    if (order.inventoryCreationMethod === 'manual' && order.inventoryTerm !== INVENTORY_TERM.LONG_TERM) {
      throw new InventoryServiceError(
        'MANUAL_INVENTORY_TERM_INVALID',
        'Manually created inventory orders must use longTerm'
      )
    }
    const sourceOrderId = asText((wrapped && item.sourceOrderId) || order.sourceOrderId).trim()
    return {order, sourceOrderId}
  })

  const sourceOrderIds = entries.map(entry => entry.sourceOrderId).filter(Boolean)
  if (sourceOrderIds.length) uniqueIds(sourceOrderIds, null, 'sourceOrderId')
  return entries
}

const validateFreshSources = async ({database, companyUID, entries}) => {
  const sourceEntries = entries.filter(entry => entry.sourceOrderId)
  if (!sourceEntries.length) return

  const freshSources = await readDeliveries(
    database,
    companyUID,
    sourceEntries.map(entry => entry.sourceOrderId)
  )
  const unavailable = freshSources.filter(entry => !entry.order)
  if (unavailable.length) {
    throw new InventoryServiceError('SOURCE_ORDER_NOT_FOUND', 'One or more source orders no longer exist', {
      orderIds: unavailable.map(entry => entry.orderId)
    })
  }
  const ineligible = freshSources.filter(entry => !canSendToInventory(entry.order))
  if (ineligible.length) {
    throw new InventoryServiceError('SOURCE_ORDER_INELIGIBLE', 'One or more source orders were already sent to inventory', {
      orderIds: ineligible.map(entry => entry.orderId)
    })
  }
}

const readNextInventoryId = async companyRef => {
  const snapshot = await companyRef.child('invNextID').once('value')
  if (!snapshotExists(snapshot)) return 1

  const value = valueFromSnapshot(snapshot)
  const parsed = Number(value)
  if (!Number.isInteger(parsed) || parsed < 1) {
    throw new InventoryServiceError('INVALID_INVENTORY_COUNTER', 'invNextID must be a positive integer', {value})
  }
  return parsed
}

const preflightInventoryIds = async (companyRef, orderIds) => {
  const results = await Promise.all(orderIds.map(async orderId => {
    const snapshot = await companyRef.child(`deliveries/${orderId}`).once('value')
    return {orderId, exists: snapshotExists(snapshot)}
  }))
  const collisions = results.filter(result => result.exists).map(result => result.orderId)
  if (collisions.length) {
    throw new InventoryServiceError(
      'INVENTORY_ID_COLLISION',
      `Allocated inventory order ID already exists: ${collisions.join(', ')}`,
      {orderIds: collisions}
    )
  }
}

export const createInventoryBatch = async options => {
  const config = options || {}
  const database = config.database || db
  const logsDatabase = config.logsDatabase === undefined
    ? (database === db ? dbLogs : null)
    : config.logsDatabase
  const companyUID = assertFirebaseKey(config.companyUID, 'companyUID')
  const entries = normalizeCreationEntries(config.orders)
  const driver = requireDriver(config.driver)
  const operator = await resolveOperator(database, companyUID, config.operator)
  const committedAt = validTimestamp(timestampOrNow(config.committedAt), 'committedAt')
  const dispatchDate = validTimestamp(
    config.dispatchDate === null || typeof config.dispatchDate === 'undefined' || config.dispatchDate === ''
      ? committedAt
      : config.dispatchDate,
    'dispatchDate'
  )
  const companyRef = database.ref(`database/transporter/${companyUID}`)

  await validateInventoryLots({database, companyUID, entries})
  await validateFreshSources({database, companyUID, entries})
  await validateInventoryVins({database, companyUID, entries})

  const counter = await readNextInventoryId(companyRef)
  const allocation = allocateInventoryIds(counter, entries.length)
  await preflightInventoryIds(companyRef, allocation.orderIds)

  const updates = {invNextID: allocation.nextInventoryId}
  const created = entries.map((entry, index) => {
    const orderId = allocation.orderIds[index]
    const cleanOrder = cleanFirebaseValue(entry.order)
    const notePush = getPushKey(companyRef.child(`deliveries/${orderId}/notes`))
    cleanOrder.notes = {
      [notePush.key]: createInventoryNote({
        date: committedAt,
        name: operator.name,
        text: buildInitialInventoryNoteText(cleanOrder)
      })
    }
    updates[`deliveries/${orderId}`] = cleanOrder

    if (entry.sourceOrderId) {
      updates[`deliveries/${entry.sourceOrderId}/sentToInventory`] = true
      updates[`deliveries/${entry.sourceOrderId}/sentToInventoryAt`] = committedAt
      updates[`deliveries/${entry.sourceOrderId}/inventoryOrderId`] = orderId
    }

    return {orderId, order: cleanOrder, sourceOrderId: entry.sourceOrderId}
  })

  await companyRef.update(updates)

  const dispatchEntries = created.map(entry => ({
    orderId: entry.orderId,
    order: entry.order,
    dispatchComment: entry.order.comment
  }))
  const dispatchResults = await queueDispatchBatch({
    database,
    companyUID,
    entries: dispatchEntries,
    driver,
    dispatchDate
  })
  const auditResults = await writeAuditLogs({
    logsDatabase,
    companyUID,
    operator,
    entries: created.map(entry => ({
      action: 'INVENTORY_CREATED',
      orderId: entry.orderId,
      sourceOrderId: entry.sourceOrderId,
      date: committedAt
    }))
  })

  return {
    created: created.map(entry => ({orderId: entry.orderId, order: entry.order})),
    createdOrderIds: created.map(entry => entry.orderId),
    nextInventoryId: allocation.nextInventoryId,
    dispatchResults,
    allDispatchesQueued: dispatchResults.every(result => result.queued),
    auditResults
  }
}

export const createManualInventoryBatch = async options => {
  const config = options || {}
  const vehicles = Array.isArray(config.vehicles) ? config.vehicles : []
  if (!vehicles.length) {
    throw new InventoryServiceError('EMPTY_SELECTION', 'At least one vehicle is required')
  }
  const enteredAt = validTimestamp(timestampOrNow(config.dateEntered), 'dateEntered')
  const orders = vehicles.map((vehicle, index) => {
    const indexedDate = config.dateEnteredByVehicleIndex && config.dateEnteredByVehicleIndex[index]
    const vehicleDate = vehicle.dateEntered !== null &&
      typeof vehicle.dateEntered !== 'undefined' &&
      vehicle.dateEntered !== ''
      ? vehicle.dateEntered
      : (indexedDate !== null && typeof indexedDate !== 'undefined' && indexedDate !== '' ? indexedDate : enteredAt)

    return buildManualInventoryOrder({
      dealer: config.dealer,
      dealerId: config.dealerId || config.payerUID,
      vehicle,
      inventoryLot: vehicle.inventoryLot || config.inventoryLot,
      inventoryTerm: vehicle.inventoryTerm || config.inventoryTerm,
      orderDetails: config.orderDetails,
      dateEntered: validTimestamp(vehicleDate, 'dateEntered'),
      dropOffPerson: config.dropOffPerson,
      dropOffOrganization: config.dropOffOrganization,
      operator: config.operator,
      zones: config.zones,
      zoneResolver: config.zoneResolver,
      transporter: config.transporter
    })
  })

  return createInventoryBatch({
    ...config,
    orders
  })
}

export const sendOrdersToInventory = async options => {
  const config = options || {}
  const database = config.database || db
  const companyUID = assertFirebaseKey(config.companyUID, 'companyUID')
  const sourceOrderIds = uniqueIds(config.sourceOrderIds, config.sourceOrderId, 'sourceOrderId')
  const enteredAt = validTimestamp(timestampOrNow(config.dateEntered), 'dateEntered')
  const freshSources = await readDeliveries(database, companyUID, sourceOrderIds)
  const missing = freshSources.filter(entry => !entry.order)
  if (missing.length) {
    throw new InventoryServiceError('SOURCE_ORDER_NOT_FOUND', 'One or more source orders no longer exist', {
      orderIds: missing.map(entry => entry.orderId)
    })
  }
  const ineligible = freshSources.filter(entry => !canSendToInventory(entry.order))
  if (ineligible.length) {
    throw new InventoryServiceError('SOURCE_ORDER_INELIGIBLE', 'One or more source orders cannot be sent to inventory', {
      orderIds: ineligible.map(entry => entry.orderId)
    })
  }

  const dealerCache = {}
  const getDealer = async sourceOrder => {
    const dealerId = asText(sourceOrder.payer).trim()
    if (!dealerId) return null
    if (Object.prototype.hasOwnProperty.call(dealerCache, dealerId)) return dealerCache[dealerId]
    const override = config.dealersByUid && config.dealersByUid[dealerId]
    if (override) {
      dealerCache[dealerId] = override
      return override
    }
    const snapshot = await database.ref(`users/dealers/${dealerId}`).once('value')
    dealerCache[dealerId] = snapshotExists(snapshot) ? valueFromSnapshot(snapshot) : null
    return dealerCache[dealerId]
  }

  const entries = []
  for (let index = 0; index < freshSources.length; index += 1) {
    const source = freshSources[index]
    const dealer = await getDealer(source.order)
    entries.push({
      sourceOrderId: source.orderId,
      order: buildInventoryOrderFromSource({
        sourceOrder: source.order,
        sourceOrderId: source.orderId,
        dealer,
        dealerId: source.order.payer,
        inventoryLot: config.inventoryLotByOrderId && config.inventoryLotByOrderId[source.orderId]
          ? config.inventoryLotByOrderId[source.orderId]
          : config.inventoryLot,
        inventoryTerm: config.inventoryTermByOrderId && config.inventoryTermByOrderId[source.orderId]
          ? config.inventoryTermByOrderId[source.orderId]
          : config.inventoryTerm,
        dateEntered: validTimestamp(
          timestampOrNow(
            config.dateEnteredByOrderId && config.dateEnteredByOrderId[source.orderId] !== undefined
              ? config.dateEnteredByOrderId[source.orderId]
              : enteredAt
          ),
          'dateEntered'
        ),
        operator: config.operator,
        zones: config.zones,
        zoneResolver: config.zoneResolver,
        transporter: config.transporter
      })
    })
  }

  return createInventoryBatch({
    ...config,
    companyUID,
    orders: entries
  })
}

export const retryInventoryDispatch = async options => {
  const config = options || {}
  const database = config.database || db
  const companyUID = assertFirebaseKey(config.companyUID, 'companyUID')
  const orderId = assertFirebaseKey(config.orderId, 'orderId')
  const driver = requireDriver(config.driver)
  const order = config.order || await readDelivery(database, companyUID, orderId)
  if (!order) {
    throw new InventoryServiceError('ORDER_NOT_FOUND', `Inventory order ${orderId} no longer exists`, {orderId})
  }
  if (!isInventoryOrder(order)) {
    throw new InventoryServiceError('NOT_INVENTORY_ORDER', `${orderId} is not an inventory order`, {orderId})
  }

  return queueDispatch({
    database,
    companyUID,
    orderId,
    order,
    driver,
    deliveryOrder: config.deliveryOrder || 1,
    comment: config.comment !== undefined ? config.comment : retryDispatchComment(order),
    dispatchDate: timestampOrNow(config.dispatchDate),
    pickDate: config.pickDate !== undefined
      ? config.pickDate
      : (order.dateLeft && order.inventoryExitRequesterName ? order.dateLeft : undefined)
  })
}

export const confirmInventoryArrival = async options => {
  const config = options || {}
  const database = config.database || db
  const logsDatabase = config.logsDatabase === undefined
    ? (database === db ? dbLogs : null)
    : config.logsDatabase
  const companyUID = assertFirebaseKey(config.companyUID, 'companyUID')
  const orderId = assertFirebaseKey(config.orderId, 'orderId')
  const confirmedAt = validTimestamp(timestampOrNow(config.confirmedAt), 'confirmedAt')
  const operator = await resolveOperator(database, companyUID, config.operator)
  const order = await readDelivery(database, companyUID, orderId)

  if (!order) {
    throw new InventoryServiceError('ORDER_NOT_FOUND', `Inventory order ${orderId} no longer exists`, {orderId})
  }
  if (!isInventoryOrder(order)) {
    throw new InventoryServiceError('NOT_INVENTORY_ORDER', `${orderId} is not an inventory order`, {orderId})
  }
  if (!canConfirmInventoryArrival(order)) {
    throw new InventoryServiceError(
      'INVENTORY_ARRIVAL_NOT_READY',
      'The inventory order must be in Pick or Transit without an inventory exit before it can be confirmed',
      {orderId}
    )
  }

  const companyRef = database.ref(`database/transporter/${companyUID}`)
  const historySnapshot = await companyRef.child(`history/${orderId}`).once('value')
  const history = valueFromSnapshot(historySnapshot) || {}
  const deliveryLatest = getLatestHistoryEntryWithKey(order.historySync)
  const historyLatest = getLatestHistoryEntryWithKey(history)
  let latest = deliveryLatest
  if (!latest || (historyLatest && historyLatest.key.localeCompare(latest.key) > 0)) latest = historyLatest

  const historyKey = latest ? assertFirebaseKey(latest.key, 'historyKey') : ''
  const deliveryHistoryEntry = historyKey && order.historySync && order.historySync[historyKey]
  const storedHistoryEntry = historyKey && history && history[historyKey]
  const historyEntry = {
    ...(deliveryHistoryEntry && typeof deliveryHistoryEntry === 'object' ? deliveryHistoryEntry : {}),
    ...(storedHistoryEntry && typeof storedHistoryEntry === 'object' ? storedHistoryEntry : {})
  }
  const dropPhotos = historyKey
    ? getValidLatestDropPhotos({historySync: {[historyKey]: historyEntry}})
    : getValidLatestDropPhotos(order)

  const notePush = getPushKey(companyRef.child(`deliveries/${orderId}/notes`))
  const updates = {
    [`deliveries/${orderId}/status`]: 'Attente',
    [`deliveries/${orderId}/inventoryInboundConfirmedAt`]: confirmedAt,
    [`deliveries/${orderId}/inventoryInboundConfirmedByUid`]: operator.uid,
    [`deliveries/${orderId}/inventoryInboundConfirmedByName`]: operator.name,
    [`deliveries/${orderId}/inventoryInboundConfirmedPhotoCount`]: dropPhotos.length,
    [`deliveries/${orderId}/notes/${notePush.key}`]: createInventoryNote({
      date: confirmedAt,
      name: operator.name,
      text: buildInventoryArrivalConfirmationNoteText(dropPhotos.length)
    })
  }

  if (historyKey) updates[`deliveries/${orderId}/inventoryInboundHistoryKey`] = historyKey
  if (historyKey && storedHistoryEntry && typeof storedHistoryEntry === 'object') {
    if (!storedHistoryEntry.dropDate) updates[`history/${orderId}/${historyKey}/dropDate`] = confirmedAt
    updates[`history/${orderId}/${historyKey}/isCancelled`] = false
  }

  await companyRef.update(updates)
  const auditResults = await writeAuditLogs({
    logsDatabase,
    companyUID,
    operator,
    entries: [{
      action: 'INVENTORY_ARRIVAL_CONFIRMED',
      orderId,
      date: confirmedAt
    }]
  })

  return {orderId, historyKey: historyKey || null, confirmedAt, photoCount: dropPhotos.length, auditResults}
}

export const updateInventoryLot = async options => {
  const config = options || {}
  const database = config.database || db
  const logsDatabase = config.logsDatabase === undefined
    ? (database === db ? dbLogs : null)
    : config.logsDatabase
  const companyUID = assertFirebaseKey(config.companyUID, 'companyUID')
  const orderId = assertFirebaseKey(config.orderId, 'orderId')
  const inventoryLot = normalizeInventoryLotName(config.inventoryLot)
  const updatedAt = validTimestamp(timestampOrNow(config.updatedAt), 'updatedAt')

  if (!isInventoryLot(inventoryLot, await loadInventoryLots(database, companyUID))) {
    throw new InventoryServiceError(
      'INVALID_INVENTORY_LOT',
      `Unknown inventory lot: ${inventoryLot}`,
      {inventoryLot: config.inventoryLot}
    )
  }

  const operator = await resolveOperator(database, companyUID, config.operator)
  const order = await readDelivery(database, companyUID, orderId)
  if (!order) {
    throw new InventoryServiceError('ORDER_NOT_FOUND', `Inventory order ${orderId} no longer exists`, {orderId})
  }
  if (!isInventoryOrder(order)) {
    throw new InventoryServiceError('NOT_INVENTORY_ORDER', `${orderId} is not an inventory order`, {orderId})
  }

  const previousLot = normalizeInventoryLotName(order.inventoryLot)
  if (previousLot === inventoryLot) {
    return {orderId, previousLot, inventoryLot, updatedAt, unchanged: true, auditResults: []}
  }

  const companyRef = database.ref(`database/transporter/${companyUID}`)
  await companyRef.update(lotChangeUpdates({companyRef, orderId, previousLot, inventoryLot, operator, updatedAt}))
  const auditResults = await writeAuditLogs({
    logsDatabase,
    companyUID,
    operator,
    entries: [{
      action: 'INVENTORY_LOT_CHANGED',
      orderId,
      date: updatedAt
    }]
  })

  return {orderId, previousLot, inventoryLot, updatedAt, unchanged: false, auditResults}
}

const lotOperationContext = async options => {
  const config = options || {}
  const database = config.database || db
  const companyUID = assertFirebaseKey(config.companyUID, 'companyUID')
  return {
    config,
    database,
    logsDatabase: config.logsDatabase === undefined
      ? (database === db ? dbLogs : null)
      : config.logsDatabase,
    companyUID,
    companyRef: database.ref(`database/transporter/${companyUID}`),
    operator: await resolveOperator(database, companyUID, config.operator)
  }
}

export const createInventoryLot = async options => {
  const {config, database, logsDatabase, companyUID, companyRef, operator} = await lotOperationContext(options)
  const name = requireInventoryLotName(config.name, 'name')
  const description = asText(config.description).trim()
  const createdAt = validTimestamp(timestampOrNow(config.createdAt), 'createdAt')
  const lots = await loadInventoryLots(database, companyUID)

  if (lots.some(lot => lot.name === name)) {
    throw new InventoryServiceError('INVENTORY_LOT_EXISTS', `Inventory lot ${name} already exists`, {inventoryLot: name})
  }

  const key = getPushKey(companyRef.child('inventoryLots')).key
  await companyRef.update({
    [`inventoryLots/${key}`]: {name, description, ...lotAuthorFields('created', operator, createdAt)}
  })

  const auditResults = await writeAuditLogs({
    logsDatabase,
    companyUID,
    operator,
    entries: [{action: 'INVENTORY_LOT_CREATED', orderId: '', inventoryLot: name, date: createdAt}]
  })

  return {lot: {id: key, name, description}, auditResults}
}

// Renaming cascades to every inventory order (active or historical) still pointing at the old name.
export const updateInventoryLotDetails = async options => {
  const {config, database, logsDatabase, companyUID, companyRef, operator} = await lotOperationContext(options)
  const updatedAt = validTimestamp(timestampOrNow(config.updatedAt), 'updatedAt')
  const name = requireInventoryLotName(config.name, 'name')
  const description = asText(config.description).trim()
  const lots = await loadInventoryLots(database, companyUID)
  const lot = findInventoryLot(lots, config.lotName)
  const previousName = lot.name

  if (name !== previousName && lots.some(item => item.name === name)) {
    throw new InventoryServiceError('INVENTORY_LOT_EXISTS', `Inventory lot ${name} already exists`, {inventoryLot: name})
  }

  const lotPath = `inventoryLots/${lot.id}`
  const authorFields = lotAuthorFields('updated', operator, updatedAt)
  const updates = Object.keys(authorFields).reduce((result, field) => {
    result[`${lotPath}/${field}`] = authorFields[field]
    return result
  }, {
    [`${lotPath}/name`]: name,
    [`${lotPath}/description`]: description
  })

  const renamedOrderIds = name === previousName
    ? []
    : (await readInventoryOrders(database, companyUID))
      .filter(entry => normalizeInventoryLotName(entry.order.inventoryLot) === previousName)
      .map(entry => entry.orderId)

  renamedOrderIds.forEach(orderId => {
    Object.assign(updates, lotChangeUpdates({
      companyRef,
      orderId,
      previousLot: previousName,
      inventoryLot: name,
      operator,
      updatedAt,
      noteText: `INVENTORY LOT RENAMED - ${previousName} -> ${name}`
    }))
  })

  await companyRef.update(updates)

  const auditResults = await writeAuditLogs({
    logsDatabase,
    companyUID,
    operator,
    entries: [{action: 'INVENTORY_LOT_UPDATED', orderId: '', inventoryLot: name, date: updatedAt}]
      .concat(renamedOrderIds.map(orderId => ({
        action: 'INVENTORY_LOT_RENAMED',
        orderId,
        inventoryLot: name,
        date: updatedAt
      })))
  })

  return {lot: {...lot, name, description}, previousName, renamedOrderIds, auditResults}
}

export const deleteInventoryLot = async options => {
  const {config, database, logsDatabase, companyUID, companyRef, operator} = await lotOperationContext(options)
  const deletedAt = validTimestamp(timestampOrNow(config.deletedAt), 'deletedAt')
  const lots = await loadInventoryLots(database, companyUID)
  const lot = findInventoryLot(lots, config.lotName)

  if (lots.length <= 1) {
    throw new InventoryServiceError('INVENTORY_LOT_LAST', 'At least one inventory lot is required', {inventoryLot: lot.name})
  }

  const activeOrderIds = (await readInventoryOrders(database, companyUID))
    .filter(entry => isActiveInventoryOrder(entry.order) && normalizeInventoryLotName(entry.order.inventoryLot) === lot.name)
    .map(entry => entry.orderId)
  if (activeOrderIds.length) {
    throw new InventoryServiceError(
      'INVENTORY_LOT_IN_USE',
      `Inventory lot ${lot.name} still has ${activeOrderIds.length} active vehicle(s)`,
      {inventoryLot: lot.name, orderIds: activeOrderIds}
    )
  }

  await companyRef.update({[`inventoryLots/${lot.id}`]: null})

  const auditResults = await writeAuditLogs({
    logsDatabase,
    companyUID,
    operator,
    entries: [{action: 'INVENTORY_LOT_DELETED', orderId: '', inventoryLot: lot.name, date: deletedAt}]
  })

  return {lot, auditResults}
}

export const transferInventoryLotOrders = async options => {
  const {config, database, logsDatabase, companyUID, companyRef, operator} = await lotOperationContext(options)
  const orderIds = uniqueIds(config.orderIds, config.orderId, 'orderId')
  const updatedAt = validTimestamp(timestampOrNow(config.updatedAt), 'updatedAt')
  const inventoryLot = normalizeInventoryLotName(config.inventoryLot)

  if (!isInventoryLot(inventoryLot, await loadInventoryLots(database, companyUID))) {
    throw new InventoryServiceError('INVALID_INVENTORY_LOT', `Unknown inventory lot: ${inventoryLot}`, {
      inventoryLot: config.inventoryLot
    })
  }

  const orders = await readDeliveries(database, companyUID, orderIds)
  const missing = orders.filter(entry => !entry.order)
  if (missing.length) {
    throw new InventoryServiceError('ORDER_NOT_FOUND', 'One or more inventory orders no longer exist', {
      orderIds: missing.map(entry => entry.orderId)
    })
  }
  const ineligible = orders.filter(entry => !isActiveInventoryOrder(entry.order))
  if (ineligible.length) {
    throw new InventoryServiceError('NOT_ACTIVE_INVENTORY_ORDER', 'Only active inventory orders can change lot', {
      orderIds: ineligible.map(entry => entry.orderId)
    })
  }

  const moved = orders
    .map(entry => ({orderId: entry.orderId, previousLot: normalizeInventoryLotName(entry.order.inventoryLot)}))
    .filter(entry => entry.previousLot !== inventoryLot)
  if (!moved.length) {
    return {inventoryLot, updatedAt, movedOrderIds: [], auditResults: []}
  }

  const updates = moved.reduce((result, entry) => Object.assign(result, lotChangeUpdates({
    companyRef,
    orderId: entry.orderId,
    previousLot: entry.previousLot,
    inventoryLot,
    operator,
    updatedAt
  })), {})
  await companyRef.update(updates)

  const auditResults = await writeAuditLogs({
    logsDatabase,
    companyUID,
    operator,
    entries: moved.map(entry => ({
      action: 'INVENTORY_LOT_CHANGED',
      orderId: entry.orderId,
      inventoryLot,
      date: updatedAt
    }))
  })

  return {inventoryLot, updatedAt, movedOrderIds: moved.map(entry => entry.orderId), auditResults}
}

export const markInventoryLeaving = async options => {
  const config = options || {}
  const database = config.database || db
  const logsDatabase = config.logsDatabase === undefined
    ? (database === db ? dbLogs : null)
    : config.logsDatabase
  const companyUID = assertFirebaseKey(config.companyUID, 'companyUID')
  const orderIds = uniqueIds(config.orderIds, config.orderId, 'orderId')
  const dateLeft = validTimestamp(timestampOrNow(config.dateLeft), 'dateLeft')
  const dispatchDate = validTimestamp(timestampOrNow(config.dispatchDate), 'dispatchDate')
  const driver = requireDriver(config.driver)
  const operator = await resolveOperator(database, companyUID, config.operator)
  const requesterName = asText(config.requesterName || (config.requester && config.requester.name)).trim()
  const requesterOrganization = asText(
    config.requesterOrganization || (config.requester && config.requester.organization)
  ).trim()
  const comment = asText(config.comment || (config.requester && config.requester.comment)).trim()
  const noteText = buildInventoryDepartureNoteText({requesterName, requesterOrganization, comment})
  const companyRef = database.ref(`database/transporter/${companyUID}`)
  const freshEntries = await readDeliveries(database, companyUID, orderIds)
  const missing = freshEntries.filter(entry => !entry.order)
  if (missing.length) {
    throw new InventoryServiceError('ORDER_NOT_FOUND', 'One or more inventory orders no longer exist', {
      orderIds: missing.map(entry => entry.orderId)
    })
  }
  const ineligible = freshEntries.filter(entry => !canLeaveInventory(entry.order))
  if (ineligible.length) {
    throw new InventoryServiceError('INVENTORY_LEAVING_INELIGIBLE', 'One or more cars cannot be marked as leaving', {
      orderIds: ineligible.map(entry => entry.orderId)
    })
  }

  const updates = {}
  const updatedEntries = freshEntries.map(entry => {
    const notePush = getPushKey(companyRef.child(`deliveries/${entry.orderId}/notes`))
    const auditFields = {
      dateLeft,
      inventoryExitRequestedByUid: operator.uid,
      inventoryExitRequestedByName: operator.name,
      inventoryExitRequesterName: requesterName,
      inventoryExitRequesterOrganization: requesterOrganization,
      inventoryExitComment: comment
    }
    Object.keys(auditFields).forEach(field => {
      updates[`deliveries/${entry.orderId}/${field}`] = auditFields[field]
    })
    updates[`deliveries/${entry.orderId}/notes/${notePush.key}`] = createInventoryNote({
      date: Date.now(),
      name: operator.name,
      text: noteText
    })
    return {
      orderId: entry.orderId,
      order: {...entry.order, ...auditFields},
      dispatchComment: joinComments(entry.order.comment, noteText),
      pickDate: dateLeft
    }
  })

  await companyRef.update(updates)

  const dispatchResults = await queueDispatchBatch({
    database,
    companyUID,
    entries: updatedEntries,
    driver,
    dispatchDate
  })
  const auditResults = await writeAuditLogs({
    logsDatabase,
    companyUID,
    operator,
    entries: orderIds.map(orderId => ({
      action: 'INVENTORY_LEAVING',
      orderId,
      date: dateLeft
    }))
  })

  return {
    updatedOrderIds: orderIds,
    dateLeft,
    dispatchResults,
    allDispatchesQueued: dispatchResults.every(result => result.queued),
    auditResults
  }
}

export const markInventoryBilled = async options => {
  const config = options || {}
  const database = config.database || db
  const logsDatabase = config.logsDatabase === undefined
    ? (database === db ? dbLogs : null)
    : config.logsDatabase
  const companyUID = assertFirebaseKey(config.companyUID, 'companyUID')
  const orderIds = uniqueIds(config.orderIds, config.orderId, 'orderId')
  const billedAt = validTimestamp(timestampOrNow(config.billedAt), 'billedAt')
  const operator = await resolveOperator(database, companyUID, config.operator)
  const billingReference = asText(config.billingReference).trim()
  const freshEntries = await readDeliveries(database, companyUID, orderIds)
  const missing = freshEntries.filter(entry => !entry.order)
  if (missing.length) {
    throw new InventoryServiceError('ORDER_NOT_FOUND', 'One or more inventory orders no longer exist', {
      orderIds: missing.map(entry => entry.orderId)
    })
  }
  const ineligible = freshEntries.filter(entry => !canBillInventory(entry.order))
  if (ineligible.length) {
    throw new InventoryServiceError('INVENTORY_BILLING_INELIGIBLE', 'One or more cars cannot be marked billed', {
      orderIds: ineligible.map(entry => entry.orderId)
    })
  }

  const companyRef = database.ref(`database/transporter/${companyUID}`)
  const updates = {}
  orderIds.forEach(orderId => {
    const notePush = getPushKey(companyRef.child(`deliveries/${orderId}/notes`))
    updates[`deliveries/${orderId}/status`] = 'Terminée'
    updates[`deliveries/${orderId}/inventoryBilledAt`] = billedAt
    updates[`deliveries/${orderId}/inventoryBilledByUid`] = operator.uid
    updates[`deliveries/${orderId}/inventoryBilledByName`] = operator.name
    updates[`deliveries/${orderId}/inventoryBillingReference`] = billingReference
    updates[`deliveries/${orderId}/notes/${notePush.key}`] = createInventoryNote({
      date: billedAt,
      name: operator.name,
      text: buildInventoryBillingNoteText(billingReference)
    })
  })

  await companyRef.update(updates)

  const auditResults = await writeAuditLogs({
    logsDatabase,
    companyUID,
    operator,
    entries: orderIds.map(orderId => ({
      action: 'INVENTORY_BILLED',
      orderId,
      date: billedAt
    }))
  })

  return {updatedOrderIds: orderIds, billedAt, auditResults}
}
